import React, {useEffect, useCallback, Fragment} from "react";
import styled from "@emotion/styled";
import {useNavigate, useParams, useOutletContext} from "react-router";
import {Button} from "@mui/material";
import {toggleBodyClass} from "modules/utils/dom_helpers";
import {CheckRounded} from "@mui/icons-material";
import {useDispatch, useSelector} from "react-redux";
import {joinToLeague} from "modules/actions/leagues";
import {getLeaguesToJoin, isLeagueRequested} from "modules/selectors";
import {MiddleLoading} from "components/MiddleLoading";
import {useTranslation} from "react-i18next";

const Wrapper = styled.div`
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;
	background: #000;
	display: flex;
	flex-flow: column;
	align-items: center;
	justify-content: center;
	gap: 20px;
	z-index: 999;
	min-height: 100%;

	@media (max-width: 880px) {
		position: fixed;
	}
`;

const Title = styled.div`
	font-weight: 700;
	font-size: 28px;
	line-height: 120%;
	text-align: center;
	padding: 0 20px;
`;

const Description = styled.div`
	font-weight: 400;
	font-size: 16px;
	line-height: 130%;
	max-width: 300px;
	text-align: center;
	margin-top: 8px;
`;

const Header = styled.div`
	display: flex;
	flex-flow: column;
	align-items: center;
	justify-content: center;

	svg {
		width: 60px;
		height: auto;
		color: #48a726;
		margin-bottom: 20px;
	}
`;
const Buttons = styled.div`
	display: flex;
	flex-flow: column;
	gap: 12px;
	max-width: 280px;
	width: 100%;

	& > button {
		width: 100%;
	}
`;

const LeagueInfo = styled.div`
	display: flex;
	flex-flow: column;
	padding: 16px 20px;
	gap: 16px;
	max-width: 380px;
	width: 100%;
	background: #292f37;
	border-radius: 8px;
`;

const InfoTitle = styled.div`
	font-weight: 700;
	font-size: 20px;
	line-height: 120%;
`;

const Table = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	gap: 16px;
	width: 100%;
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 4px;
	flex: 1;

	p {
		&:first-of-type {
			font-weight: 700;
		}
	}

	&:first-of-type {
		p {
			&:last-of-type {
				text-transform: capitalize;
			}
		}
	}
`;
const useJoinByCode = () => {
	const context = useOutletContext<{onClose?: () => void}>();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const i18n = useTranslation();
	const leagueToJoin = useSelector(getLeaguesToJoin);
	const isLoading = useSelector(isLeagueRequested);
	const {code = ""} = useParams<{code: string}>();
	const currentLeague = leagueToJoin.leagues.find(
		(it) => it.code.toLowerCase() === code.toLowerCase().trim()
	);

	useEffect(() => {
		toggleBodyClass("disable_scroll", true, "join");
		return () => {
			toggleBodyClass("disable_scroll", false, "join");
		};
	}, []);

	const toLeague = () => currentLeague && navigate(`/groups/${currentLeague.id}/table`);

	const onJoin = useCallback(() => {
		if (currentLeague) {
			dispatch(
				joinToLeague({
					leagueCode: currentLeague.code,
					onSuccess: toLeague,
				})
			);
		}
	}, [dispatch, currentLeague]);

	const onClose = () => {
		navigate("../");
		context.onClose?.();
	};

	useEffect(() => {
		if (!currentLeague) {
			navigate("../");
		}
	}, [currentLeague, navigate]);

	return {
		isLoading,
		currentLeague,
		onJoin,
		onClose,
		i18n,
		toLeague,
	};
};

const JoinGroupByCode: React.FC = () => {
	const {isLoading, currentLeague, onJoin, onClose, i18n, toLeague} = useJoinByCode();

	return (
		<Wrapper>
			<MiddleLoading when={isLoading} size={20} />
			<Header>
				<CheckRounded />
				<Title>{currentLeague?.name}</Title>
				{currentLeague?.isJoined ? (
					<Description>
						{i18n.t("leagues.join_code.title_joined", "You’re already in the group")}
					</Description>
				) : (
					<Description>
						{i18n.t(
							"leagues.join_code.title",
							"Congrats - You’ve been invited to join a group."
						)}
					</Description>
				)}
			</Header>
			<Buttons>
				{currentLeague?.isJoined ? (
					<Button variant={"contained"} onClick={toLeague}>
						{i18n.t("leagues.join_code.view_button", "View group")}
					</Button>
				) : (
					<Fragment>
						<Button variant={"contained"} onClick={onClose}>
							{i18n.t("leagues.join_code.no_button", "No, Cancel")}
						</Button>
						<Button variant={"contained"} color={"secondary"} onClick={onJoin}>
							{i18n.t("leagues.join_code.yes_button", "Yes, Join")}
						</Button>
					</Fragment>
				)}
			</Buttons>

			<LeagueInfo>
				<InfoTitle>{i18n.t("leagues.join_code.settings", "Settings")}</InfoTitle>

				<Table>
					<Column>
						<p>{i18n.t("leagues.join_code.group_type", "Group Type")}</p>
						<p>{currentLeague?.privacy}</p>
					</Column>
					<Column>
						<p>{i18n.t("leagues.join_code.chairman", "Chairman")}</p>
						<p>{currentLeague?.username}</p>
					</Column>
				</Table>
			</LeagueInfo>
		</Wrapper>
	);
};

export default JoinGroupByCode;
